import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  ActivityIndicator,
  ScrollView,
  TouchableOpacity,
  Platform,
  Alert,
  Image,
  ImageBackground,
} from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import type { V2Balance, V2CardDetailResponse } from '../../types/v2';
import TabBar from '../../components/TabBar';
import SimpleHeader from '../../components/SimpleHeader';
import { COLORS } from '../../theme/themeTokens';
import { v2GetCardWallet, v2GetCardTransactions } from '../../services/v2/api';

// ──────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────

interface CardScreenProps {
  balance: V2Balance;
  cardDetail: V2CardDetailResponse | null;
  cardDetailLoading: boolean;
  onBack: () => void;
  onNavigate: (screen: string) => void;
  onTransactionPress: (tx: any) => void;
  /** Tapped from the inline "Deposit" CTA — routes to Operations on the
   *  Top-up tab. Optional only for backwards compatibility; parents that
   *  miss the prop will land in the no-op default branch (logged warning). */
  onDeposit?: () => void;
}

// ──────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────

const CardScreen: React.FC<CardScreenProps> = ({ balance, cardDetail, cardDetailLoading, onBack, onNavigate, onTransactionPress, onDeposit }) => {
  const statusBarHeight = Platform.OS === 'android' ? 28 : 0;
  
  const [walletName, setWalletName] = useState<string | null>(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  // Filter oставлен на текущем 'all' — фильтр-buttons в JSX уже нет,
  // seter не понадобится. Держим useState чтобы useMemo ниже читал stable ref.
  const [txFilter] = useState<'all' | 'deposit' | 'withdraw'>('all');

  // Infinite-scroll pagination — рендерим первые N транзакций, при
  // near-bottom scroll бампаем на +10. Данные все уже в state (backend
  // aggregate'ит 90-day dump в одну пачку), поэтому bump — синхронный
  // setState без async fetch'а. Reset на новый card (сменился wallet_address).
  const PAGE_SIZE = 10;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [balance.wallet_address]);

  // OTP + sensitive card fields (Number/CVV/PIN/Expire) + PIN modal переехали
  // на CardInfoScreen (title "Card details"). Здесь оставлены только hero +
  // Top-up + More info + история. См. plan в чате.
  useEffect(() => {
    let mounted = true;
    const cardId = balance.wallet_address ?? '';
    if (!cardId) { return; }

    const fetchWallet = async () => {
      try {
        setWalletLoading(true);
        const res = await v2GetCardWallet(cardId);
        const name = res?.data?.name ?? null;
        if (mounted) setWalletName(name);
      } catch (err) {
        console.warn('[CardScreen] v2GetCardWallet failed for', cardId, err);
      } finally {
        if (mounted) setWalletLoading(false);
      }
    };

    const fetchTransactions = async () => {
      try {
        setTxLoading(true);
        const pad = (n: number) => String(n).padStart(2, '0');
        const fmt = (d: Date) =>
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        const now = new Date();
        const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        const res = await v2GetCardTransactions(cardId, fmt(ninetyDaysAgo), fmt(now));
        // Раньше был `.slice(-10)` — обрезал 90-day dump до последних 10.
        // Теперь храним все, пагинация на клиенте (visibleCount + onScroll
        // near-bottom detection). Backend endpoint agregate'ит все страницы
        // Payoma; для типичного user'а 90-day объём умещается в памяти без
        // проблем. Если появятся heavy users — переходим на server-side
        // pagination (см. Вариант B в плане).
        const txList = Array.isArray(res?.transactions) ? res.transactions : [];
        if (mounted) setTransactions(txList);
      } catch (err) {
        console.warn('[CardScreen] v2GetCardTransactions failed for', cardId, err);
      } finally {
        if (mounted) setTxLoading(false);
      }
    };

    fetchWallet();
    fetchTransactions();
    return () => { mounted = false; };
  }, [balance.wallet_address]);

  // Amount from card detail (path: data.wallet.balance.amount)
  // Precision defines the number of decimal places (e.g. precision=2 → divide by 100)
  const _rawAmount = cardDetail?.data?.wallet?.balance?.amount ?? null;
  const _precision: number = cardDetail?.data?.wallet?.currency?.precision ?? 2;
  const liveAmount = _rawAmount != null ? _rawAmount / Math.pow(10, _precision) : null;
  // Payoma answers this screen live, and a refused call used to leave the
  // hero showing "€ ,00" — an empty number, not a zero balance. The balance
  // the backend keeps synced stands in for it, flagged as not fresh.
  const storedAmount = parseFloat(balance.available_amount ?? balance.amount ?? '');
  const amountFromDetail = liveAmount
    ?? (Number.isFinite(storedAmount) ? storedAmount : null);
  const showingStoredAmount = liveAmount == null && amountFromDetail != null;
  // Blocked status from card detail (path: data.blocked). Управляет только
  // badge на hero-card'e — Top-up остаётся активным даже при blocked (per
  // mockup 2). Detail-level actions (Unblock / OTP / sensitive fields)
  // теперь живут в CardInfoScreen.
  const isCardBlocked: boolean = cardDetail?.data?.blocked === true;

  /** Format number with space as thousands separator and dot as decimal separator.
   *  e.g. 2785 → "2 785.00", 1234567.5 → "1 234 567.50" */
  const formatAmount = (value: number | string | null): string => {
    if (value == null) return '';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return String(value);
    const [intPart, decPart] = num.toFixed(2).split('.');
    const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${withSpaces},${decPart}`;
  };

  // Format "YYYY-MM-DD" → "dd.MM.yyyy"
  const formatDate = (iso: string): string => {
    const parts = iso.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return iso;
  };

  /** Extract the raw numeric amount from a transaction */
  const getTxRawAmount = (tx: any): number => {
    if (typeof tx.amount === 'object' && tx.amount !== null) {
      return Number(tx.amount.amount ?? 0);
    }
    return Number(tx.amount ?? 0);
  };

  // Group transactions by date (YYYY-MM-DD part of created_at), applying
  // current filter. Пагинация: сортируем всё → slice(0, visibleCount) →
  // group. Так группы содержат ровно то что видно (не рисуем «пустые» дни
  // будущих страниц).
  const filteredSorted = React.useMemo(() => {
    const filtered = txFilter === 'all'
      ? [...transactions]
      : transactions.filter((tx) => {
          const raw = getTxRawAmount(tx);
          return txFilter === 'deposit' ? raw > 0 : raw < 0;
        });
    filtered.sort((a, b) => {
      const dateA = a.created_at ?? '';
      const dateB = b.created_at ?? '';
      return dateB.localeCompare(dateA);
    });
    return filtered;
  }, [transactions, txFilter]);

  const totalTx = filteredSorted.length;
  const hasMore = visibleCount < totalTx;

  const groupedTransactions = React.useMemo(() => {
    const visible = filteredSorted.slice(0, visibleCount);
    const groups: { date: string; items: any[] }[] = [];
    const map = new Map<string, any[]>();
    for (const tx of visible) {
      const createdAt: string = tx.created_at ?? '';
      const dateKey = createdAt.split(' ')[0] || createdAt; // "YYYY-MM-DD"
      if (!map.has(dateKey)) {
        const items: any[] = [];
        map.set(dateKey, items);
        groups.push({ date: formatDate(dateKey), items });
      }
      map.get(dateKey)!.push(tx);
    }
    return groups;
  }, [filteredSorted, visibleCount]);

  // ScrollView near-bottom detection — бампаем visibleCount на PAGE_SIZE.
  // Sync setState: данные уже в memory, никаких fetch'ей. Guard hasMore
  // не даёт бесконечно бампать когда всё показано.
  const handleScroll = React.useCallback((e: { nativeEvent: { contentOffset: { y: number }; contentSize: { height: number }; layoutMeasurement: { height: number } } }) => {
    if (!hasMore) return;
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    const paddingToBottom = 200;
    if (contentOffset.y + layoutMeasurement.height >= contentSize.height - paddingToBottom) {
      setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, totalTx));
    }
  }, [hasMore, totalTx]);

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />

      <View style={[styles.fixedHeader, { paddingTop: statusBarHeight }]}>
        <SimpleHeader
          title="Card details"
          onBack={onBack}
          backgroundVariant="primaryNavy"
        />
      </View>

      <View style={styles.bottomOverscrollCap} />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        // 200ms throttle — реагируем на near-bottom без нагружения на
        // каждый scroll frame. Бамп visibleCount синхронный, no fetch.
        scrollEventThrottle={200}
      >
        {(cardDetailLoading || walletLoading) ? (
          <ActivityIndicator size="large" color={COLORS.brandOrange} style={styles.loader} />
        ) : (
          <ImageBackground
            source={require('../../assets/product.png')}
            style={styles.infoCard}
            imageStyle={styles.infoCardBgImage}
            resizeMode="cover"
          >
            <View style={styles.infoCardInner}>
              <View style={styles.amountRow}>
                <Text style={styles.infoLabelLight}>€ </Text>
                <Text style={styles.infoAmountLight}>
                  {formatAmount(amountFromDetail).split(',')[0]}
                </Text>
                <Text style={styles.infoAmountFraction}>
                  ,{formatAmount(amountFromDetail).split(',')[1] ?? '00'}
                </Text>
              </View>
              {showingStoredAmount ? (
                <Text style={styles.amountStaleNote}>Balance may be out of date</Text>
              ) : null}
              <Text style={styles.walletLabel}>Wallet</Text>
              <View style={styles.walletValueFrame}>
                <Text style={styles.infoValueLight}>{walletName ?? ''}</Text>
                <TouchableOpacity
                  style={styles.frameCopyBtn}
                  activeOpacity={0.7}
                  onPress={() => {
                    const val = walletName ?? '';
                    if (val) {
                      Clipboard.setString(val);
                      Alert.alert('Copied', 'Wallet name copied to clipboard');
                    }
                  }}
                >
                  <Image
                    source={require('../../assets/copy.png')}
                    style={styles.copyIcon}
                  />
                </TouchableOpacity>
              </View>
            </View>
            {isCardBlocked && (
              <View style={styles.blockedBadge}>
                <Text style={styles.blockedBadgeText}>Blocked</Text>
              </View>
            )}
          </ImageBackground>
        )}

        {/* Action row — Top-up + More info side by side (per mockup 1/2).
            Иконки 32×32 внутри кнопок; label как отдельный Text под каждой.
            Top-up остаётся активным даже для blocked карты (mockup 2).
            More info открывает CardInfoScreen с OTP / sensitive fields /
            Block-action / limits. */}
        <View style={styles.actionRow}>
          <View style={styles.actionItem}>
            <TouchableOpacity
              style={styles.actionBtnTopUp}
              activeOpacity={0.85}
              onPress={() => {
                if (onDeposit) {
                  onDeposit();
                } else {
                  console.warn('[CardScreen] Top-up pressed but onDeposit prop is missing');
                }
              }}
            >
              <Image source={require('../../assets/arrowup.png')} style={styles.actionIcon} resizeMode="contain" />
            </TouchableOpacity>
            <Text style={styles.actionBtnLabel}>Top-up</Text>
          </View>
          <View style={styles.actionItem}>
            <TouchableOpacity
              style={styles.actionBtnMoreInfo}
              activeOpacity={0.85}
              onPress={() => onNavigate('cardInfo')}
            >
              <Image source={require('../../assets/creditcard_white.png')} style={styles.actionIcon} resizeMode="contain" />
            </TouchableOpacity>
            <Text style={styles.actionBtnLabel}>More info</Text>
          </View>
        </View>

        {/* History frame */}
        {txLoading ? (
          <ActivityIndicator size="small" color={COLORS.brandOrange} style={styles.loader} />
        ) : (
          <View style={styles.historyCard}>
            <View style={styles.historyHeaderRow}>
              <TouchableOpacity style={styles.historyLinkRow} onPress={() => onNavigate('cardHistory')} activeOpacity={0.7}>
                <Text style={styles.historyLink}>History</Text>
                <Image source={require('../../assets/arrow_left_20_white.png')} style={styles.historyArrowIcon} />
              </TouchableOpacity>
            </View>
            {groupedTransactions.length === 0 ? (
              <Text style={styles.historyEmpty}>No transactions</Text>
            ) : (
              <>
                {groupedTransactions.map((group) => (
                  <View key={group.date} style={styles.historyGroup}>
                    <Text style={styles.historyDate}>{group.date}</Text>
                      {group.items.map((tx: any, idx: number) => (
                        <TouchableOpacity
                          key={tx.id ?? idx}
                          style={styles.historyRow}
                          onPress={() => onTransactionPress(tx)}
                          activeOpacity={0.7}
                        >
                          <Image
                            source={require('../../assets/creditcard_white.png')}
                            style={styles.historyRowIcon}
                          />
                          {/* The merchant, and nothing under it. The kind
                              of operation — "Card (POS wireless)" — used
                              to sit here in grey; on the card's own screen
                              it says nothing a client acts on, and where
                              it was worth replacing, in the full history,
                              the card's own number took its place. The
                              element goes rather than its text: an empty
                              Text still holds a line's height. */}
                          <View style={styles.historyLeft}>
                            <Text style={styles.historyName} numberOfLines={1}>
                              {tx.terminal?.name ?? 'Deposit'}
                            </Text>
                          </View>

                          <Text style={[styles.historyAmount, getTxRawAmount(tx) > 0 && styles.historyAmountPositive]}>
                            {(() => {
                              const raw = getTxRawAmount(tx);
                              const prefix = raw > 0 ? '+' : '';
                              const formatted = typeof tx.amount === 'object' && tx.amount !== null
                                ? `${formatAmount((tx.amount.amount ?? 0) / 100)} ${tx.amount.currency ?? ''}`
                                : `${formatAmount((tx.amount ?? 0) / 100)} ${tx.currency ?? ''}`;
                              return `${prefix}${formatted}`;
                            })()}
                          </Text>
                        </TouchableOpacity>
                      ))}
                  </View>
                ))}
                {!hasMore && totalTx > 0 ? (
                  <Text style={styles.historyEndFooter}>No more transactions</Text>
                ) : null}
              </>
            )}
          </View>
        )}

      </ScrollView>

      <TabBar activeTab="dashboard" onNavigate={onNavigate} />
    </View>
  );
};

// ──────────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },

  // ── Fixed header (outside scroll) ──────────────────────
  fixedHeader: {
    backgroundColor: COLORS.primaryNavy,
  },

  // ── Outer scroll (flex container) ──────────────────────
  scrollView: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  bottomOverscrollCap: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 500,
    backgroundColor: COLORS.pureLight,
  },
  scrollContent: {
    flexGrow: 1,
    backgroundColor: COLORS.primaryNavy,
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 120,
  },
  
  // ── Top section (dark) ──────────────────────────────────
  loader: {
    marginVertical: 40,
  },

  // ── Cards/Sections ──────────────────────────────────────
  infoCard: {
    width: '100%',
    borderRadius: 8,
    marginBottom: 12,
    overflow: 'hidden',
  },
  // Action row — Top-up + More info side by side (mockup 1/2). Кнопки
  // растягиваются flex:1 чтобы левые/правые края совпадали с hero-card'ом
  // и history-frame'ом (padding scrollContent). Раньше был fixed
  // width: 175 — на iPhone 375pt (SE, mini) 175+20+175=370 > 335 usable
  // → overflow; на iPhone Pro Max (428pt) наоборот gap по краям слишком
  // большой. Height: 60 фиксированный per design.
  actionRow: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 12,
  },
  // actionItem — обёртка кнопка+label. flex:1 растягивает обёртки
  // равномерно; alignItems:'stretch' даёт кнопке внутри 100% ширины
  // обёртки. Label остаётся center'ом благодаря textAlign:'center'.
  actionItem: {
    flex: 1,
    alignItems: 'stretch',
  },
  actionBtnTopUp: {
    height: 60,
    backgroundColor: COLORS.brandOrange,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionBtnMoreInfo: {
    height: 60,
    backgroundColor: COLORS.steelNavy,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionIcon: {
    width: 32,
    height: 32,
    tintColor: COLORS.white,
  },
  actionBtnLabel: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.white,
    textAlign: 'center',
    marginTop: 8,
  },
  infoCardBgImage: {
    borderRadius: 8,
  },
  blockedBadge: {
    position: 'absolute',
    bottom: 20,
    right: 16,
    width: 85,
    height: 36,
    backgroundColor: '#CD0A0A',
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  blockedBadgeText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    fontFamily: 'Inter',
  },
  infoCardInner: {
    padding: 16,
    alignItems: 'flex-start',
  },
  amountRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: 12,
  },
  amountStaleNote: {
    fontFamily: 'Inter',
    fontSize: 12,
    lineHeight: 16,
    color: 'rgba(255,255,255,0.7)',
    marginTop: 4,
  },
  walletLabel: {
    fontSize: 14,
    lineHeight: 16,
    color: COLORS.blueSteel,
    fontWeight: '400',
    marginTop: 4,
  },
  // ── Info rows ───────────────────────────────────────────
  infoLabelLight: {
    fontSize: 32,
    lineHeight: 48,
    color: COLORS.white,
    fontWeight: '600',
  },
  infoAmountLight: {
    fontSize: 32,
    lineHeight: 48,
    color: COLORS.white,
    fontWeight: '600',
  },
  infoAmountFraction: {
    fontSize: 18,
    lineHeight: 48,
    color: COLORS.blueSteel,
    fontWeight: '600',
  },
  infoValueLight: {
    fontSize: 18,
    lineHeight: 22,
    color: COLORS.white,
    fontWeight: '500',
  },
  walletValueFrame: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: COLORS.primaryNavy,
    borderRadius: 8,
    paddingLeft: 12,
    paddingRight: 4,
    paddingVertical: 8,
    marginTop: 8,
  },
  frameCopyBtn: {
    padding: 4,
  },
  copyIcon: {
    width: 20,
    height: 20,
    tintColor: COLORS.blueSteel,
  },

  // ── History section ─────────────────────────────────────
  historyCard: {
    width: '100%',
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
  },
  historyHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  historyLinkRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  historyLink: {
    fontSize: 22,
    lineHeight: 32,
    fontWeight: '500',
    color: COLORS.steelNavy,
  },
  historyArrowIcon: {
    width: 16,
    height: 16,
    marginLeft: 6,
    tintColor: COLORS.steelNavy,
    transform: [{ rotate: '180deg' }],
  },
  historyEmpty: {
    fontSize: 16,
    color: COLORS.blueSteel,
    textAlign: 'center',
    paddingVertical: 12,
  },
  historyEndFooter: {
    fontSize: 13,
    color: COLORS.blueSteel,
    textAlign: 'center',
    paddingVertical: 12,
    marginTop: 4,
  },
  historyGroup: {
    marginBottom: 8,
  },
  historyDate: {
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '400',
    color: COLORS.blueSteel,
    marginBottom: 6,
  },
  historyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  historyName: {
    flex: 1,
    fontSize: 18,
    lineHeight: 22,
    color: COLORS.primaryNavy,
    fontWeight: '400',
  },
  historyAmount: {
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '500',
    color: COLORS.primaryNavy,
  },
  historyAmountPositive: {
    color: COLORS.mint,
  },
  historyLeft: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  historyRowIcon: {
    width: 28,
    height: 28,
    marginRight: 10,
    tintColor: COLORS.steelNavy,
  },

});

export default CardScreen;
